import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { RadioGroup } from '@atlaskit/radio';
import Button, { LinkButton } from '@atlaskit/button/new';
import Link from '@atlaskit/link';
import Form, { Field, FormFooter } from '@atlaskit/form';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

const i18n = defineMessages({
	title: {
		id: 'team-admin-controls.admin-controls.page.title',
		defaultMessage: 'Teams',
		description: 'Page title for the admin controls page',
	},
	manageTeamsButton: {
		id: 'team-admin-controls.admin-controls.page.manage-teams-button',
		defaultMessage: 'Manage teams',
		description: 'Button to manage teams',
	},
	subtextPrefix: {
		id: 'team-admin-controls.admin-controls.page.subtext-prefix',
		defaultMessage:
			'By default, users on this site can use teams to manage access to spaces and content. You can choose which team types can be assigned access.',
		description: 'Subtext for the admin controls page',
	},
	learnMoreLink: {
		id: 'team-admin-controls.admin-controls.page.learn-more-link',
		defaultMessage: 'Learn more',
		description: 'Learn more link',
	},
	radioGroupLabel: {
		id: 'team-admin-controls.admin-controls.page.radio-group-label',
		defaultMessage: 'Which teams can be assigned access?',
		description: 'Label for the radio group',
	},
	radioGroupOption1: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-1',
		defaultMessage: 'Any team type',
		description: 'Option 1 for the radio group',
	},
	radioGroupOption2: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-2',
		defaultMessage: 'Only verified teams',
		description: 'Option 2 for the radio group',
	},
	radioGroupOption3: {
		id: 'team-admin-controls.admin-controls.page.radio-group-option-3',
		defaultMessage: 'Only specific types',
		description: 'Option 3 for the radio group',
	},
	saveButton: {
		id: 'team-admin-controls.admin-controls.page.save-button',
		defaultMessage: 'Save',
		description: 'Save button',
	},
	cancelButton: {
		id: 'team-admin-controls.admin-controls.page.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Cancel button',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.800'),
		paddingInline: token('space.800'),
		maxWidth: '1000px',
		marginInline: 'auto',
	},
});

const radioOptions = [
	{
		value: 'all',
		label: <FormattedMessage {...i18n.radioGroupOption1} />,
	},
	{
		value: 'verified',
		label: <FormattedMessage {...i18n.radioGroupOption2} />,
	},
	{
		value: 'specific',
		label: <FormattedMessage {...i18n.radioGroupOption3} />,
	},
];

export const AdminControls = () => {
	const { cloudId, orgId } = useSessionData();
	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container}>
			<Stack space="space.300">
				{/* Header with title and Manage teams button */}
				<Flex justifyContent="space-between" alignItems="center">
					<Heading size="large">{formatMessage(i18n.title)}</Heading>
					<LinkButton iconAfter={LinkExternalIcon} appearance="subtle" href={href} target="_blank">
						{formatMessage(i18n.manageTeamsButton)}
					</LinkButton>
				</Flex>

				{/* Subtext with Learn more link */}
				<Text>
					{formatMessage(i18n.subtextPrefix)}{' '}
					<Link href="https://confluence.atlassian.com/conf59/manage-teams-1024324379.html">
						{formatMessage(i18n.learnMoreLink)}
					</Link>
				</Text>

				{/* Form with radio group */}
				<Box paddingBlockStart="space.300">
					<Form
						onSubmit={() => {
							// TODO: Implement save functionality
							return Promise.resolve();
						}}
					>
						{({ formProps }) => (
							<form {...formProps}>
								<Field
									label={formatMessage(i18n.radioGroupLabel)}
									name="teamAccessType"
									defaultValue="all"
								>
									{({ fieldProps }) => <RadioGroup {...fieldProps} options={radioOptions} />}
								</Field>
								<FormFooter>
									<Button type="submit" appearance="primary">
										{formatMessage(i18n.saveButton)}
									</Button>
									<Button type="button" appearance="subtle">
										{formatMessage(i18n.cancelButton)}
									</Button>
								</FormFooter>
							</form>
						)}
					</Form>
				</Box>
			</Stack>
		</Box>
	);
};
